// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-for-in-array, react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

import React, { useEffect, useState } from "react";
import { Segment, Loader } from "semantic-ui-react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useSelector } from "react-redux";

import AFPlot from "../../../../Lumber/Plotting/AFPlotly";
import "./style.css";
import {
	NEUTRAL_CORRELATION_COLOR,
	NEGATIVE_CORRELATION_COLOR,
	POSITIVE_CORRELATION_COLOR,
} from "../../../../../utilities/graphics.ts";
import formatPlannedTiming from "../FormatPlannedTiming";
import calculateOverlappingCorrelationValue from "../CalculateOverlappingCorrelationValue";

const toRgb = (c) => `rgb(${c.red},${c.green},${c.blue})`;

const FullCorrelationMatrix = ({ growthCurveData, groundData, groundDataSelected, selectedAnchor, colors }) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);
	const [fullPearsonData, setFullPearsonData] = useState(null);
	const [fullPearsonLoading, setFullPearsonLoading] = useState(true);
	const [displayNameMap, setDisplayNameMap] = useState({});

	function getFullPearsonData() {
		let coefficientData = [];
		const dnMap = {};
		let groupedGrowthCurveData = _.groupBy(growthCurveData, "displayName");

		for (let gcd of growthCurveData) {
			if (gcd.curveModelDisplayName) {
				dnMap[gcd.displayName] = gcd.curveModelDisplayName;
			}
		}

		setDisplayNameMap(dnMap);

		let structuredRawData = [];
		let values = [];
		_.forEach(groupedGrowthCurveData, (entry, key) => {
			if (dnMap[key]) {
				values = [];
				entry.forEach((obj) => {
					values.push({
						plotRange: obj.plotRange,
						plotColumn: obj.plotColumn,
						value: obj.value,
						plotId: obj.plotId,
						excludeFromAssessment: obj.excludeFromAssessment,
					});
				});
				structuredRawData[key] = values;
			}
		});

		if (groundDataSelected) {
			const groundDataSetGroupedData = _.groupBy(groundData, "groundDatasetId");
			_.forEach(groundDataSetGroupedData, (groundDatasetGroup) => {
				const averagedAssessmentIds = _(groundDatasetGroup)
					.filter((obj) => obj["averageFor"] != null)
					.uniqBy((obj) => obj["averageFor"])
					.map("averageFor")
					.value();

				let assessmentGroupedData = _.groupBy(groundDatasetGroup, "groundDataAssessmentId");
				_.forEach(assessmentGroupedData, (assessmentGroup) => {
					let firstAssessmentRecordInGroup = assessmentGroup[0];
					if (!_.includes(averagedAssessmentIds, firstAssessmentRecordInGroup["groundDataAssessmentId"])) {
						values = [];
						assessmentGroup.forEach((obj) => {
							values.push({
								plotRange: obj.plotRange,
								plotColumn: obj.plotColumn,
								value: obj.value,
								excludeFromAssessment: obj.excludeFromAssessment,
							});
						});

						let display = formatPlannedTiming(
							firstAssessmentRecordInGroup["assessmentName"],
							firstAssessmentRecordInGroup["timingMethodType"],
							firstAssessmentRecordInGroup["timingMethodName"],
							firstAssessmentRecordInGroup["growthStageName"],
							firstAssessmentRecordInGroup["growthPhaseInteger"],
							firstAssessmentRecordInGroup["growthPhaseStart"],
							firstAssessmentRecordInGroup["growthPhaseEnd"],
						);

						structuredRawData[display] = values;
					}
				});
			});
		}

		for (const columnA in structuredRawData) {
			coefficientData[columnA] = [];
			for (const columnB in structuredRawData) {
				let correlationValue = calculateOverlappingCorrelationValue(
					structuredRawData[columnA],
					structuredRawData[columnB],
				);
				if (correlationValue < -1) correlationValue = -1;
				else if (correlationValue > 1) correlationValue = 1;
				coefficientData[columnA][columnB] = correlationValue;
			}
		}

		setFullPearsonData(coefficientData);
		setFullPearsonLoading(false);
	}

	function getSortedKeysListForAnchor(coefficientData, targetAnchor) {
		if (targetAnchor !== "") {
			if (_.includes(Object.keys(coefficientData), targetAnchor)) {
				return Object.keys(coefficientData[targetAnchor])
					.map((key) => [key, coefficientData[targetAnchor][key]])
					.sort((a, b) => b[1] - a[1])
					.map((item) => item[0]);
			}
			return Object.keys(coefficientData);
		}
		return [];
	}

	useEffect(() => {
		getFullPearsonData();
	}, [growthCurveData, groundData, groundDataSelected, selectedAnchor]);

	const colorscale = [
		[0, toRgb(colors.negativeColor)],
		[0.5, toRgb(colors.neutralColor)],
		[1, toRgb(colors.positiveColor)],
	];

	const allSortedKeys =
		!fullPearsonLoading && fullPearsonData ? getSortedKeysListForAnchor(fullPearsonData, selectedAnchor) : [];

	const sortedKeys =
		allSortedKeys.length <= 1
			? allSortedKeys
			: allSortedKeys.filter((key) =>
					Object.keys(fullPearsonData[key]).some(
						(otherKey) => otherKey !== key && !isNaN(fullPearsonData[key][otherKey]),
					),
				);
	const n = sortedKeys.length;
	const labels = sortedKeys.map((k) => displayNameMap[k] ?? k);
	const z = sortedKeys.map((rowKey) => sortedKeys.map((colKey) => fullPearsonData[rowKey][colKey]));
	const text = z.map((row) => row.map((v) => (isNaN(v) ? "-" : v.toFixed(3))));

	return (
		<Segment basic>
			{fullPearsonLoading ? (
				<Loader active={true} />
			) : (
				<div className="correlationMatrixPlotContainer">
					<AFPlot
						disableHoverStyle
						data={[
							{
								type: "heatmap",
								z,
								x: labels,
								y: labels,
								text,
								texttemplate: "%{text}",
								colorscale,
								zmin: -1,
								zmax: 1,
								showscale: false,
								xgap: 1,
								ygap: 1,
								hovertemplate: "%{y} × %{x}: %{text}<extra></extra>",
							},
						]}
						layout={{
							autosize: true,
							margin: { l: 20, r: 20, t: 20, b: 20 },
							xaxis: {
								side: "top",
								tickangle: 45,
								automargin: true,
								range: [-0.5, n - 0.5],
								constrain: "domain",
								constraintoward: "left",
							},
							yaxis: {
								range: [n - 0.5, -0.5],
								automargin: true,
								scaleanchor: "x",
								scaleratio: 1,
								constrain: "domain",
								constraintoward: "top",
							},
							paper_bgcolor: "transparent",
							plot_bgcolor: "transparent",
						}}
						config={{
							responsive: true,
							toImageButtonOptions: { filename: `FullCorrelationMatrix_${entityNames?.trialName}` },
						}}
						useResizeHandler
						style={{ width: "100%", height: "100%" }}
					/>
				</div>
			)}
		</Segment>
	);
};

FullCorrelationMatrix.propTypes = {
	growthCurveData: PropTypes.array,
	groundData: PropTypes.array,
	groundDataSelected: PropTypes.bool,
	selectedAnchor: PropTypes.string,
	colors: PropTypes.object,
};

FullCorrelationMatrix.defaultProps = {
	growthCurveData: [],
	groundData: [],
	groundDataSelected: false,
	selectedAnchor: "",
	colors: {
		negativeColor: NEGATIVE_CORRELATION_COLOR,
		neutralColor: NEUTRAL_CORRELATION_COLOR,
		positiveColor: POSITIVE_CORRELATION_COLOR,
	},
};

export default FullCorrelationMatrix;
